import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Box } from '@availity/element';
import { useNavigate } from 'react-router-dom';
import subscriptionApi from '../../../api/developerHub/subscriptionApi';
import { typography } from '../../../static/constants/typography';
import { colors } from '../../../static/constants/theme';
import { themeColors, iconFilters } from '../../../static/constants/styles';
import { useSessionStore, useAppStore } from '../../../hooks';
import type { SessionStore } from '../../../auth/sessionService';
import handleExpiredAuthAPIResponse from '../../Utils/apiResponseValidator';
import arrowSort from '../../../static/assets/icons/arrow-sort.svg';
import noSubscriptionIcon from '../../../static/assets/icons/no-subscription.svg';
import noSubscriptionLightIcon from '../../../static/assets/icons/no-subscription-light.svg';
import { trackEvent } from '../../Utils/splunkEventHelper';
import { isFeatureEnabled } from '../../Utils/featureFlags';


interface AccessHubTabProps {
  theme: 'light' | 'dark';
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' };
const HEADERS = ['Organization', 'Plans', 'Effective Date'] as const;
type Header = (typeof HEADERS)[number];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const TableCell = React.memo(({ cellText, borderColor, children }: { cellText: string; borderColor: string; children: React.ReactNode }) => (
  <Box
    sx={{
      p: '10px 16px',
      ...typography.latoRegular16Alt,
      color: cellText,
      borderRight: `1px solid ${borderColor}`,
      minWidth: 0,
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    }}
  >
    {children}
  </Box>
));

const AccessHubTab = ({ theme }: AccessHubTabProps) => {
  const themeStyle = themeColors[theme];
  const navigate = useNavigate();
  const sessionStore = useSessionStore();
  const { subscriptionDetails, subscriptionDetailsLoaded, setSubscriptionDetails } = useAppStore((store) => store);
  const [loading, setLoading] = useState(!subscriptionDetailsLoaded);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ column: Header; asc: boolean } | null>({ column: 'Effective Date', asc: false });

  useEffect(() => {
    if (!isFeatureEnabled('thalesImplementation') && subscriptionDetailsLoaded) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await subscriptionApi.getSubscriptionDetails();
        // Added below validation, since on session expiry forgerock(PING) flow, throws 200 with html respone
        if (!isFeatureEnabled('thalesImplementation') && !handleExpiredAuthAPIResponse(data, sessionStore as SessionStore, navigate)) return;
        if (!Array.isArray(data)) {
          throw new TypeError('Invalid response from subscription API');
        }
        setSubscriptionDetails(data);
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // if(isFeatureEnabled('thalesImplementation') && (status === 401 || status === 403)) {
        //   handleExpiredAuthAPIResponse(null, sessionStore as SessionStore, navigate, status);
        //   return;
        // }
        setFetchError((error as Error).message || 'Failed to load subscriptions');
        trackEvent({
          event: 'subscription-fetch-failed',
          status,
          api: 'subscription-details-api',
        });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [subscriptionDetailsLoaded, setSubscriptionDetails, sessionStore, navigate]);

  const mappedSubscriptions = useMemo(
    () =>
      (Array.isArray(subscriptionDetails) ? subscriptionDetails : []).map((item) => ({
        id: item.key_id,
        organization: item.org_name ?? '',
        plan: item.key_type ? capitalize(item.key_type) : '',
        expirationMs: new Date(item.expiration_time).getTime(),
        effectiveDate: new Date(item.expiration_time).toLocaleDateString('en-US', DATE_FORMAT),
      })),
    [subscriptionDetails]
  );

  const equalColumns = useMemo(() => {
    if (mappedSubscriptions.length <= 1) return new Set<Header>(HEADERS);
    const first = mappedSubscriptions[0];
    const equal = new Set<Header>();
    if (mappedSubscriptions.every((item) => item.organization.toLowerCase() === first.organization.toLowerCase())) equal.add('Organization');
    if (mappedSubscriptions.every((item) => item.plan.toLowerCase() === first.plan.toLowerCase())) equal.add('Plans');
    if (mappedSubscriptions.every((item) => item.expirationMs === first.expirationMs)) equal.add('Effective Date');
    return equal;
  }, [mappedSubscriptions]);

  const handleSort = useCallback((header: Header) => {
    if (equalColumns.has(header)) return;
    setSort((p) => ({ column: header, asc: p?.column === header ? !p.asc : false }));
  }, [equalColumns]);

  const sortedSubscriptions = useMemo(() => {
    if (!sort) return mappedSubscriptions;
    const { column, asc } = sort;
    return [...mappedSubscriptions].sort((a, b) => {
      let cmp = 0;
      switch (column) {
        case 'Effective Date':
          cmp = a.expirationMs - b.expirationMs;
          break;
        case 'Organization':
          cmp = a.organization.localeCompare(b.organization);
          break;
        case 'Plans':
          cmp = a.plan.localeCompare(b.plan);
          break;
        default:
          break;
      }
      return asc ? cmp : -cmp;
    });
  }, [mappedSubscriptions, sort]);

  if (fetchError) throw new Error(fetchError);

  return (
    <Box sx={{
      p: { xs: '24px 16px', sm: '40px 32px', md: '64px 155px' },
      bgcolor: themeStyle.background,
      minHeight: 'calc(100vh - 120px)',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <Box sx={{ mb: '39px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box component="h1" sx={{ ...typography.latoSemibold28, letterSpacing: 0, color: themeStyle.headingColor, m: 0 }}>
          My Subscriptions
        </Box>
        <Box sx={{ ...typography.latoRegular16Alt, letterSpacing: 0, color: themeStyle.headingColor, m: 0 }}>
          API Subscription Information
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress size={32} sx={{ color: colors.text.secondary }} />
        </Box>
      ) : sortedSubscriptions.length === 0 ? (
        <Box sx={{
          border: `1px solid ${themeStyle.emptyStateBorder}`,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: '300px', md: '533px' },
          gap: '16px',
        }}>
          <img src={noSubscriptionLightIcon} alt="no subscriptions" width="100" height="100" style={{ display: theme === 'light' ? 'block' : 'none' }} />
          <img src={noSubscriptionIcon} alt="no subscriptions" width="100" height="100" style={{ display: theme === 'dark' ? 'block' : 'none' }} />
          <Box sx={{ ...typography.montSemibold20, color: themeStyle.cellText, px: '16px', py: '8px', borderRadius: '4px' }}>
            You have no subscriptions at this time.
          </Box>
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${themeStyle.border}`, borderRadius: '8px', overflow: { xs: 'auto', md: 'hidden' } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              minWidth: { xs: '470px', md: 'unset' },
              bgcolor: themeStyle.cardBackground,
              borderBottom: `1px solid ${themeStyle.border}`,
            }}
          >
            {HEADERS.map((header) => (
              <Box
                key={header}
                sx={{
                  p: '13px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  ...typography.latoRegular16,
                  letterSpacing: 0,
                  color: themeStyle.text,
                  borderRight: `1px solid ${themeStyle.border}`,
                }}
              >
                {header}
                <Box
                  component="span"
                  onClick={() => handleSort(header)}
                  sx={{ cursor: equalColumns.has(header) ? 'default' : 'pointer', display: 'flex' }}
                >
                  <img
                    data-testid={`sort-icon-${header.toLowerCase().replace(' ', '-')}`}
                    src={arrowSort}
                    alt="sort"
                    width="18"
                    height="18"
                    style={{
                      filter: theme === 'light' ? iconFilters.dark : iconFilters.light,
                      opacity: sort?.column === header ? 1 : equalColumns.has(header) ? 0.2 : 0.4,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {sortedSubscriptions.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                minWidth: { xs: '470px', md: 'unset' },
                bgcolor: themeStyle.background,
                '&:not(:last-child)': { borderBottom: `1px solid ${themeStyle.border}` },
              }}
            >
              <TableCell cellText={themeStyle.cellText} borderColor={themeStyle.border}>{row.organization}</TableCell>
              <TableCell cellText={themeStyle.cellText} borderColor={themeStyle.border}>{row.plan}</TableCell>
              <TableCell cellText={themeStyle.cellText} borderColor={themeStyle.border}>{row.effectiveDate}</TableCell>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AccessHubTab;
